import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { format } from 'date-fns';
import {
  Loader2,
  LayoutGrid,
  Globe,
  Share2,
  TrendingUp,
  TrendingDown,
  BarChart3,
  RefreshCw,
} from 'lucide-react';
import { cn, formatNumber, formatCurrency } from '../lib/utils';

const DV360BreakdownTabs = ({ dateRange }) => {
  const [activeTab, setActiveTab] = useState('formats');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    formats: [],
    sources: [],
    domains: [],
    totalFormats: 0,
    totalSources: 0,
    totalDomains: 0,
  });

  const tabs = [
    { id: 'formats', label: 'Formats', icon: LayoutGrid, description: 'Creative types (Display, Video, etc.)' },
    { id: 'sources', label: 'Sources', icon: Share2, description: 'Exchanges (YouTube, Google Ad Manager, etc.)' },
    { id: 'domains', label: 'Domains', icon: Globe, description: 'Sites where ads were shown' },
  ];

  const fetchData = async () => {
    if (!dateRange?.from || !dateRange?.to) {
      toast.error('Please select a date range first');
      return;
    }

    setLoading(true);
    try {
      const startDate = format(dateRange.from, 'yyyy-MM-dd');
      const endDate = format(dateRange.to, 'yyyy-MM-dd');
      
      const response = await api.get(`/dv360/source-domain-by-format`, {
        params: { start_date: startDate, end_date: endDate }
      });

      if (response.data) {
        setData({
          formats: response.data.formats || [],
          sources: response.data.sources || [],
          domains: response.data.domains || [],
          totalFormats: response.data.total_formats || 0,
          totalSources: response.data.total_sources || 0,
          totalDomains: response.data.total_domains || 0,
        });
        toast.success('DV360 breakdown data loaded');
      }
    } catch (error) {
      console.error('Error fetching DV360 breakdown:', error);
      toast.error(error.response?.data?.detail || 'Failed to fetch DV360 breakdown data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals for the current tab
  const calculateTotals = (items) => {
    return items.reduce(
      (acc, item) => ({
        impressions: acc.impressions + (item.impressions || 0),
        clicks: acc.clicks + (item.clicks || 0),
        spend: acc.spend + (item.spend || 0),
        conversions: acc.conversions + (item.conversions || 0),
      }),
      { impressions: 0, clicks: 0, spend: 0, conversions: 0 }
    );
  };

  const getTabData = () => {
    switch (activeTab) {
      case 'formats':
        return data.formats;
      case 'sources':
        return data.sources;
      case 'domains':
        return data.domains;
      default:
        return [];
    }
  };

  const getNameField = () => {
    switch (activeTab) {
      case 'formats':
        return 'format';
      case 'sources':
        return 'source';
      case 'domains':
        return 'domain';
      default:
        return 'name';
    }
  };

  const tabData = getTabData();
  const totals = calculateTotals(tabData);
  const nameField = getNameField();

  return (
    <Card className="border-slate-200 mt-4">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            DV360 Breakdown Reports
            <span className="text-sm font-normal text-slate-500">
              (via Bid Manager API)
            </span>
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            data-testid="fetch-dv360-breakdown-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Fetch Data
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Tabs */}
        <div className="flex gap-2 mb-4 border-b border-slate-200 pb-3">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const count = tab.id === 'formats' ? data.totalFormats : 
                         tab.id === 'sources' ? data.totalSources : data.totalDomains;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`dv360-tab-${tab.id}`}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                )}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {count > 0 && (
                  <span className={cn(
                    'px-1.5 py-0.5 rounded text-xs',
                    activeTab === tab.id ? 'bg-blue-500' : 'bg-slate-200'
                  )}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Description */}
        <p className="text-xs text-slate-500 mb-4">
          {tabs.find(t => t.id === activeTab)?.description}
        </p>

        {/* Summary Cards */}
        {tabData.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 uppercase">Total Impressions</p>
              <p className="text-lg font-semibold text-slate-900">{formatNumber(totals.impressions)}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 uppercase">Total Clicks</p>
              <p className="text-lg font-semibold text-slate-900">{formatNumber(totals.clicks)}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 uppercase">Total Spend</p>
              <p className="text-lg font-semibold text-slate-900">{formatCurrency(totals.spend, 'USD')}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 uppercase">Total Conversions</p>
              <p className="text-lg font-semibold text-slate-900">{formatNumber(totals.conversions)}</p>
            </div>
          </div>
        )}

        {/* Data Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-slate-600">Fetching data from Google Bid Manager API...</span>
          </div>
        ) : tabData.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p>Click "Fetch Data" to load {activeTab} breakdown</p>
            <p className="text-xs mt-2">Data is fetched from Google Bid Manager API for the selected date range</p>
          </div>
        ) : (
          <div className="overflow-auto max-h-[400px] border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">#</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700 min-w-[200px]">
                    {activeTab === 'formats' ? 'Format' : activeTab === 'sources' ? 'Source' : 'Domain'}
                  </th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Impressions</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Clicks</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Spend</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">CTR</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">CPC</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">CPM</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Conversions</th>
                </tr>
              </thead>
              <tbody>
                {tabData.map((item, index) => {
                  const spendPercent = totals.spend > 0 ? ((item.spend / totals.spend) * 100).toFixed(1) : 0;
                  return (
                    <tr 
                      key={`${item[nameField]}-${index}`}
                      className="border-b border-slate-100 hover:bg-slate-50"
                    >
                      <td className="py-2.5 px-4 text-slate-400">{index + 1}</td>
                      <td className="py-2.5 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900 truncate max-w-[250px]" title={item[nameField]}>
                            {item[nameField]}
                          </span>
                          {parseFloat(spendPercent) >= 10 && (
                            <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                              {spendPercent}%
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2.5 px-4 text-right font-mono">{formatNumber(item.impressions)}</td>
                      <td className="py-2.5 px-4 text-right font-mono">{formatNumber(item.clicks)}</td>
                      <td className="py-2.5 px-4 text-right font-mono font-medium">{formatCurrency(item.spend, 'USD')}</td>
                      <td className="py-2.5 px-4 text-right font-mono">{item.ctr?.toFixed(2)}%</td>
                      <td className="py-2.5 px-4 text-right font-mono">{formatCurrency(item.cpc || 0, 'USD')}</td>
                      <td className="py-2.5 px-4 text-right font-mono">{formatCurrency(item.cpm || 0, 'USD')}</td>
                      <td className="py-2.5 px-4 text-right font-mono">{formatNumber(item.conversions)}</td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Totals Row */}
              <tfoot className="sticky bottom-0 bg-slate-100 font-semibold">
                <tr className="border-t-2 border-slate-300">
                  <td className="py-3 px-4"></td>
                  <td className="py-3 px-4 text-slate-700">Total ({tabData.length} items)</td>
                  <td className="py-3 px-4 text-right font-mono">{formatNumber(totals.impressions)}</td>
                  <td className="py-3 px-4 text-right font-mono">{formatNumber(totals.clicks)}</td>
                  <td className="py-3 px-4 text-right font-mono">{formatCurrency(totals.spend, 'USD')}</td>
                  <td className="py-3 px-4 text-right font-mono">
                    {totals.impressions > 0 ? ((totals.clicks / totals.impressions) * 100).toFixed(2) : 0}%
                  </td>
                  <td className="py-3 px-4 text-right font-mono">
                    {formatCurrency(totals.clicks > 0 ? totals.spend / totals.clicks : 0, 'USD')}
                  </td>
                  <td className="py-3 px-4 text-right font-mono">
                    {formatCurrency(totals.impressions > 0 ? (totals.spend / totals.impressions) * 1000 : 0, 'USD')}
                  </td>
                  <td className="py-3 px-4 text-right font-mono">{formatNumber(totals.conversions)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DV360BreakdownTabs;
